import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { FU_STATUSES } from "../lib/constants";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Sel, Field } from "./Sel";

export const isDone = (fu) => (fu.status || "Pending") !== "Pending";

export const fuState = (fu) => {
  if (isDone(fu)) return "done";
  if (fu.date && fu.date.slice(0, 10) < new Date().toISOString().slice(0, 10)) return "overdue";
  return fu.date ? "scheduled" : "empty";
};

const STATE_UI = {
  done: { cls: "border-emerald-500 bg-emerald-500 text-white" },
  overdue: { cls: "border-rose-400 bg-rose-50 text-rose-600", label: "Overdue" },
  scheduled: { cls: "border-amber-400 bg-amber-50 text-amber-700", label: "Scheduled" },
  empty: { cls: "border-slate-200 bg-white text-slate-400", label: "Not set" },
};

export const FollowUpStepper = ({ followups }) => (
  <div className="flex items-center overflow-x-auto pb-1" data-testid="followup-stepper">
    {followups.map((fu, i) => (
      <div key={fu.no} className="flex items-center flex-1 last:flex-none min-w-[44px]">
        <div title={fu.status} className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 text-sm font-bold ${STATE_UI[fuState(fu)].cls}`}>{fu.no}</div>
        {i < followups.length - 1 && <div className={`h-0.5 flex-1 ${isDone(fu) ? "bg-emerald-500" : "bg-slate-200"}`} />}
      </div>
    ))}
  </div>
);

export const FollowUpSlot = ({ leadId, fu, onSaved, readOnly }) => {
  const [f, setF] = useState(fu);
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(fu), [fu]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const st = fuState(fu);

  const run = async (fn, msg) => {
    setBusy(true);
    try { onSaved((await fn()).data); toast.success(msg); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const save = () => run(() => api.put(`/leads/${leadId}/followups/${fu.no}`, f), `Follow-up #${fu.no} saved`);
  const remove = () => run(() => api.delete(`/leads/${leadId}/followups/${fu.no}`), `Follow-up #${fu.no} removed`);

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-3" data-testid={`lead-followup-slot-${fu.no}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold">Follow-up #{fu.no}</div>
        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATE_UI[st].cls}`} data-testid={`followup-${fu.no}-state`}>
          {st === "done" ? fu.status : STATE_UI[st].label}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field label="Follow-up date"><Input disabled={readOnly} data-testid={`followup-${fu.no}-date`} type="date" value={(f.date || "").slice(0, 10)} onChange={set("date")} /></Field>
        <Field label="Status"><Sel testid={`followup-${fu.no}-status`} value={f.status} onChange={set("status")} options={FU_STATUSES} /></Field>
        <Field label="Next follow-up date"><Input disabled={readOnly} data-testid={`followup-${fu.no}-next-date`} type="date" value={f.next_followup_date || ""} onChange={set("next_followup_date")} /></Field>
      </div>
      <Field label="Remarks"><Textarea disabled={readOnly} data-testid={`followup-${fu.no}-remarks`} rows={2} value={f.remarks || ""} onChange={set("remarks")} /></Field>
      {!readOnly && (
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] text-slate-400">{fu.updated_by ? `Last updated by ${fu.updated_by}` : ""}</div>
          <div className="flex gap-2">
            {fu.no > 5 && <Button size="sm" variant="outline" className="text-rose-600" disabled={busy} onClick={remove} data-testid={`followup-${fu.no}-delete-btn`}><Trash2 className="h-4 w-4" /></Button>}
            <Button size="sm" disabled={busy} onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`followup-${fu.no}-save-btn`}>Save</Button>
          </div>
        </div>
      )}
    </div>
  );
};
